import {
  ALLOWED_TERM_MONTHS,
  GUEST_FINANCING_DEFAULTS,
  loadGuestFinancingPrefs,
} from "./buyerFinancingPrefsStorage";
import { formatPriceCop } from "../automapListing/listingFormatters";

export function resolveFinancingPrefsForEstimate(rawPrefs = {}, slug = null) {
  const guestPrefs = slug ? loadGuestFinancingPrefs(slug) : null;
  const merged = {
    ...GUEST_FINANCING_DEFAULTS,
    ...(guestPrefs || {}),
    ...(rawPrefs || {}),
  };

  let termMonths = Number(merged.termMonths);
  if (!ALLOWED_TERM_MONTHS.includes(termMonths)) {
    termMonths = GUEST_FINANCING_DEFAULTS.termMonths;
  }

  let annualInterestRate = Number(merged.annualInterestRate);
  if (!Number.isFinite(annualInterestRate) || annualInterestRate < 0 || annualInterestRate > 60) {
    annualInterestRate = GUEST_FINANCING_DEFAULTS.annualInterestRate;
  }

  let downPaymentCop = merged.downPaymentCop != null ? Number(merged.downPaymentCop) : 0;
  if (!Number.isFinite(downPaymentCop) || downPaymentCop < 0) {
    downPaymentCop = 0;
  }

  return {
    downPaymentCop: Math.round(downPaymentCop),
    termMonths,
    annualInterestRate,
  };
}

export function financingPrefsLookSaved(prefs = {}) {
  if (!prefs || typeof prefs !== "object") return false;
  return Boolean(prefs.updatedAt || prefs.source === "buyer_preferences");
}

export function isDownPaymentMissing(financingPrefs = {}, slug = null) {
  const guestPrefs = slug ? loadGuestFinancingPrefs(slug) : null;
  const merged = {
    ...GUEST_FINANCING_DEFAULTS,
    ...(guestPrefs || {}),
    ...(financingPrefs || {}),
  };
  const down = merged.downPaymentCop;
  if (down == null || down === "") return true;
  const parsed = Number(down);
  return !Number.isFinite(parsed) || parsed <= 0;
}

export function calculateMonthlyPaymentEstimate({
  vehiclePriceCop,
  downPaymentCop = 0,
  termMonths = GUEST_FINANCING_DEFAULTS.termMonths,
  annualInterestRate = GUEST_FINANCING_DEFAULTS.annualInterestRate,
} = {}) {
  const price = Number(vehiclePriceCop);
  if (!Number.isFinite(price) || price <= 0) {
    return null;
  }

  const term = Number(termMonths);
  if (!Number.isFinite(term) || term <= 0) {
    return null;
  }

  const down = Math.max(0, Math.round(Number(downPaymentCop) || 0));
  const principal = Math.max(0, Math.round(price) - down);
  const rate = Number(annualInterestRate);
  const safeRate = Number.isFinite(rate) && rate >= 0 && rate <= 60 ? rate : GUEST_FINANCING_DEFAULTS.annualInterestRate;

  let monthlyPaymentCop = 0;
  if (principal > 0) {
    const monthlyRate = safeRate / 100 / 12;
    if (monthlyRate === 0 || safeRate === 0) {
      monthlyPaymentCop = Math.round(principal / term);
    } else {
      const factor = (1 + monthlyRate) ** term;
      monthlyPaymentCop = Math.round((principal * monthlyRate * factor) / (factor - 1));
    }
  }

  return {
    vehiclePriceCop: Math.round(price),
    downPaymentCop: down,
    principalCop: principal,
    termMonths: term,
    annualInterestRate: safeRate,
    monthlyPaymentCop,
  };
}

export function getVehiclePaymentEstimate(vehiclePriceCop, financingPrefs = {}, options = {}) {
  const resolved = resolveFinancingPrefsForEstimate(financingPrefs, options.slug);
  const estimate = calculateMonthlyPaymentEstimate({
    vehiclePriceCop,
    ...resolved,
  });
  if (!estimate) {
    return null;
  }
  return estimate;
}

export function formatMonthlyPaymentEstimate(estimate, options = {}) {
  const { formatMoney, currency = "COP", t } = options;
  if (!estimate || estimate.monthlyPaymentCop == null) {
    return t ? t("automotive.paymentEstimate.unavailable") : "Payment estimate unavailable";
  }
  const amount = formatPriceCop(estimate.monthlyPaymentCop, currency, { formatMoney });
  const suffix = t ? t("automotive.paymentEstimate.perMonth") : "/month";
  return `${amount}${suffix}`;
}

export function vehiclePriceBucket(priceCop) {
  const price = Number(priceCop);
  if (!Number.isFinite(price) || price <= 0) return "unknown";
  if (price < 20000000) return "under_20m";
  if (price < 50000000) return "20m_50m";
  if (price < 100000000) return "50m_100m";
  return "100m_plus";
}
